<template>
  <div>
    <!-- 邮箱验证模态窗 -->
    <EmailVerification 
      v-if="showEmailVerification"
      @close="closeEmailVerification"
      @success="handleEmailVerificationSuccess"
    />

    <!-- 捐赠弹窗 -->
    <Teleport to="body">
      <Transition name="donation-modal">
        <DonationModal v-if="showDonation" @close="closeDonationModal" />
      </Transition>
    </Teleport>

    <!-- 意见反馈弹窗 -->
    <Teleport to="body">
      <Transition name="feedback-modal">
        <FeedbackModal v-if="showFeedback" @close="closeFeedbackModal" />
      </Transition>
    </Teleport>

    <!-- 纪录片收录弹窗 -->
    <Teleport to="body">
      <div v-if="showSubmission" class="submission-modal" @click="closeSubmissionModal">
        <div class="submission-modal-content" @click.stop>
          <DocumentarySubmission @close="closeSubmissionModal" />
        </div>
      </div>
    </Teleport>

    <!-- 播放平台选择弹窗 -->
    <Teleport to="body">
      <Transition name="play-platform-modal">
        <PlayPlatformModal 
          v-if="showPlayPlatform" 
          :documentary-title="playPlatformDocumentaryTitle"
          :video-u-rls="playPlatformVideoURLs"
          @close="closePlayPlatformModal" 
        />
      </Transition>
    </Teleport>

    <!-- 消息提示弹窗 -->
    <Teleport to="body">
      <Transition name="message-modal">
        <div v-if="showMessage" class="message-modal" @click="closeMessage">
          <div class="message-modal-content" @click.stop>
            <div class="message-icon">
              <i :class="messageType === 'error' ? 'fas fa-exclamation-circle' : 'fas fa-info-circle'"></i>
            </div>
            <h3 class="message-title">{{ messageTitle }}</h3>
            <p class="message-text">{{ messageContent }}</p>
            <button class="message-btn" @click="closeMessage">知道了</button>
          </div>
        </div>
      </Transition>
    </Teleport>

  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import EmailVerification from '@/components/auth/EmailVerification.vue'
import DocumentarySubmission from '@/components/DocumentarySubmission.vue'
import DonationModal from '@/components/DonationModal.vue'
import FeedbackModal from '@/components/FeedbackModal.vue'
import PlayPlatformModal from '@/components/PlayPlatformModal.vue'
import type { DocumentaryVideoURL } from '@/types'

// 邮箱验证模态窗状态
const showEmailVerification = ref(false)
const emailVerificationResolve = ref<((value: any) => void) | null>(null)

// 捐赠弹窗状态
const showDonation = ref(false)

// 意见反馈弹窗状态
const showFeedback = ref(false)

// 纪录片收录弹窗状态
const showSubmission = ref(false)

// 播放平台选择弹窗状态
const showPlayPlatform = ref(false)
const playPlatformDocumentaryTitle = ref('')
const playPlatformVideoURLs = ref<DocumentaryVideoURL[]>([])

// 打开邮箱验证模态窗
const openEmailVerification = (): Promise<any> => {
  return new Promise((resolve) => {
    emailVerificationResolve.value = resolve
    showEmailVerification.value = true
  })
}

// 关闭邮箱验证模态窗
const closeEmailVerification = () => {
  showEmailVerification.value = false
  if (emailVerificationResolve.value) {
    emailVerificationResolve.value(undefined)
    emailVerificationResolve.value = null
  }
}

// 邮箱验证成功处理
const handleEmailVerificationSuccess = (result: any) => {
  if (emailVerificationResolve.value) {
    emailVerificationResolve.value(result)
    emailVerificationResolve.value = null
  }
  showEmailVerification.value = false
  
  // 触发成功事件
  const event = new CustomEvent('email-verification-success', { detail: result })
  window.dispatchEvent(event)
}

// 捐赠弹窗方法
const openDonation = () => {
  showDonation.value = true
}

const closeDonationModal = () => {
  showDonation.value = false
}

// 意见反馈弹窗方法
const openFeedback = () => {
  showFeedback.value = true
}

const closeFeedbackModal = () => {
  showFeedback.value = false
}

// 纪录片收录弹窗方法
const openSubmission = () => {
  showSubmission.value = true
}

const closeSubmissionModal = () => {
  showSubmission.value = false
}

// 播放平台选择弹窗方法
const openPlayPlatform = (documentaryTitle?: string, videoURLs?: DocumentaryVideoURL[]) => {
  playPlatformDocumentaryTitle.value = documentaryTitle || ''
  playPlatformVideoURLs.value = videoURLs || []
  showPlayPlatform.value = true
}

const closePlayPlatformModal = () => {
  showPlayPlatform.value = false
  playPlatformDocumentaryTitle.value = ''
  playPlatformVideoURLs.value = []
}

// 消息提示弹窗状态
const showMessage = ref(false)
const messageTitle = ref('')
const messageContent = ref('')
const messageType = ref<'info' | 'error'>('info')

// 打开消息提示弹窗
const showMessageModal = (title: string, content: string, type: 'info' | 'error' = 'info') => {
  messageTitle.value = title
  messageContent.value = content
  messageType.value = type
  showMessage.value = true
}

// 关闭消息提示弹窗
const closeMessage = () => {
  showMessage.value = false
}

onMounted(() => {
  // 初始化全局modal
})

// 暴露方法给全局使用
;(window as any).globalModal = {
  openEmailVerification,
  openDonation,
  closeDonationModal,
  openFeedback,
  closeFeedbackModal,
  openSubmission,
  closeSubmissionModal,
  openPlayPlatform,
  closePlayPlatformModal,
  showMessage: showMessageModal
}
</script>

<style scoped>
/* 纪录片收录弹窗样式 */
.submission-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
  padding: 20px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.submission-modal-content {
  background: white;
  border-radius: 20px;
  width: min(900px, 95vw);
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  z-index: var(--z-modal);
}

/* 弹窗动画效果 */
.donation-modal-enter-active,
.donation-modal-leave-active {
  transition: all 0.3s ease;
}

.donation-modal-enter-from,
.donation-modal-leave-to {
  opacity: 0;
}

.donation-modal-enter-active .donation-modal-content,
.donation-modal-leave-active .donation-modal-content {
  transition: all 0.3s ease;
}

.donation-modal-enter-from .donation-modal-content {
  transform: scale(0.8) translateY(50px);
}

.donation-modal-leave-to .donation-modal-content {
  transform: scale(0.8) translateY(50px);
}

/* 播放平台弹窗动画效果 */
.play-platform-modal-enter-active,
.play-platform-modal-leave-active {
  transition: all 0.3s ease;
}

.play-platform-modal-enter-from,
.play-platform-modal-leave-to {
  opacity: 0;
}

.play-platform-modal-enter-active .play-platform-content,
.play-platform-modal-leave-active .play-platform-content {
  transition: all 0.3s ease;
}

.play-platform-modal-enter-from .play-platform-content {
  transform: scale(0.8) translateY(50px);
}

.play-platform-modal-leave-to .play-platform-content {
  transform: scale(0.8) translateY(50px);
}

/* 消息提示弹窗样式 */
.message-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
  padding: 20px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.message-modal-content {
  background: white;
  border-radius: 20px;
  width: min(400px, 90vw);
  padding: 40px 30px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: messageSlideIn 0.3s ease;
}

@keyframes messageSlideIn {
  from {
    opacity: 0;
    transform: translateY(-30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.message-icon {
  font-size: 3em;
  margin-bottom: 20px;
  color: var(--secondary-color);
}

.message-icon .fa-exclamation-circle {
  color: var(--warning-color);
}

.message-title {
  font-size: 1.4em;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 15px;
}

.message-text {
  font-size: 1em;
  color: var(--dark-text);
  line-height: 1.6;
  margin-bottom: 25px;
}

.message-btn {
  background: var(--gradient-primary);
  color: white;
  border: none;
  padding: 12px 40px;
  border-radius: 25px;
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.message-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

/* 消息弹窗动画 */
.message-modal-enter-active,
.message-modal-leave-active {
  transition: all 0.3s ease;
}

.message-modal-enter-from,
.message-modal-leave-to {
  opacity: 0;
}

.message-modal-enter-active .message-modal-content,
.message-modal-leave-active .message-modal-content {
  transition: all 0.3s ease;
}

.message-modal-enter-from .message-modal-content {
  transform: scale(0.9) translateY(20px);
}

.message-modal-leave-to .message-modal-content {
  transform: scale(0.9) translateY(20px);
}

/* 反馈弹窗动画效果 */
.feedback-modal-enter-active,
.feedback-modal-leave-active {
  transition: all 0.3s ease;
}

.feedback-modal-enter-from,
.feedback-modal-leave-to {
  opacity: 0;
}

.feedback-modal-enter-active .feedback-modal-content,
.feedback-modal-leave-active .feedback-modal-content {
  transition: all 0.3s ease;
}

.feedback-modal-enter-from .feedback-modal-content {
  transform: scale(0.8) translateY(50px);
}

.feedback-modal-leave-to .feedback-modal-content {
  transform: scale(0.8) translateY(50px);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .submission-modal-content {
    width: min(800px, 95vw);
  }

  .message-modal-content {
    padding: 30px 20px;
  }

  .message-icon {
    font-size: 2.5em;
  }

  .message-title {
    font-size: 1.2em;
  }
}
</style>